import timeEntryUtils from "../../../shared/utils/timeEntry";
import {
  EasyTimePickerComposablesData,
  EasyTimePickerInputEmits,
  EasyTimePickerInputProps,
  InputRef,
  TimeParts,
} from "../../types/easyTimePicker";
import { useFocusEvents } from "./focusEvents";
import { useGetSetHelpers } from "./getSetHelpers";

export const useKeyboardDownEvents = (
  props: EasyTimePickerInputProps,
  emit: EasyTimePickerInputEmits,
  data: EasyTimePickerComposablesData
) => {
  const { focusAndSetCursor, handleClickOutsideTimeParts, triggerInputBlur, dispatchInputEvent } = useFocusEvents(
    props,
    emit,
    data
  );

  const { getInputRef, getInputValue } = useGetSetHelpers(props, emit, data);

  const resolveCursorPosition = (part: TimeParts) => {
    const input = getInputRef(part);
    if (!input?.value) return { isCursorAtTheStart: false, isCursorAtTheEnd: false };

    const cursorPosition = input.value.selectionStart;
    const isTextSelected = input.value.selectionStart !== input.value.selectionEnd;
    return {
      isCursorAtTheStart: cursorPosition === 0 && !isTextSelected,
      isCursorAtTheEnd: cursorPosition === getInputValue(part).length && !isTextSelected,
    };
  };

  const proceedBack = (e: KeyboardEvent, part: TimeParts): void => {
    const { isCursorAtTheStart } = resolveCursorPosition(part);
    if (!isCursorAtTheStart) return;

    e.preventDefault();
    if (part === TimeParts.Minutes) {
      triggerInputBlur(TimeParts.Minutes);
      focusAndSetCursor(TimeParts.Hours, props.hoursValue.length);
    }
    if (part === TimeParts.Meridiem) {
      triggerInputBlur(TimeParts.Meridiem);
      focusAndSetCursor(TimeParts.Minutes, props.minutesValue.length);
    }
  };

  const handleTabKey = (e: KeyboardEvent, part: TimeParts, isCursorAtTheEnd = false) => {
    if (e.shiftKey) {
      part === TimeParts.Hours ? triggerInputBlur(part) : proceedBack(e, part);
      return;
    }

    proceedForward(e, part, isCursorAtTheEnd);
  };

  const proceedForward = (e: KeyboardEvent, part: TimeParts, isCursorAtTheEnd = false): void => {
    if (isCursorAtTheEnd && !isLastInput(part)) e.preventDefault();

    triggerInputBlur(part);
    if (part === TimeParts.Hours) proceedForwardFromHours(e);
    if (part === TimeParts.Minutes) proceedForwardFromMinutes(e);
  };

  const isLastInput = (part: TimeParts) => {
    return (!props.useMeridiem && part === TimeParts.Minutes) || part === TimeParts.Meridiem;
  };

  const proceedForwardFromHours = (e: KeyboardEvent): void => {
    e.preventDefault();
    focusAndSetCursor(TimeParts.Minutes, 0);
  };

  const proceedForwardFromMinutes = (e: KeyboardEvent): void => {
    if (props.useMeridiem) {
      e.preventDefault();
      focusAndSetCursor(TimeParts.Meridiem, 0);
    } else {
      handleClickOutsideTimeParts(TimeParts.Minutes);
    }
  };

  const adjustByUnit = (e: KeyboardEvent, part: TimeParts) => {
    e.preventDefault();
    const unit = e.key === "ArrowDown" ? -1 : 1;
    const input = getInputRef(part) as InputRef;
    if (!input.value) return;

    if (part === TimeParts.Meridiem) {
      input.value.value = getInputValue(part) === "pm" ? "am" : "pm";
    } else {
      input.value.value = timeEntryUtils.padTimePart(+getInputValue(part) + unit);
    }

    dispatchInputEvent(input, "input");
  };

  const proceedEsc = () => emit("blur");

  const keyActions: Record<string, (e: KeyboardEvent, part: TimeParts, isCursorAtTheEnd: boolean) => void> = {
    Backspace: proceedBack,
    ArrowLeft: proceedBack,
    Enter: proceedForward,
    ArrowRight: proceedForward,
    Tab: handleTabKey,
    ArrowUp: adjustByUnit,
    ArrowDown: adjustByUnit,
    Escape: proceedEsc,
  };

  const handleKeyDown = (e: KeyboardEvent, part: TimeParts) => {
    const { isCursorAtTheEnd } = resolveCursorPosition(part);
    if (keyActions[e.key]) keyActions[e.key](e, part, isCursorAtTheEnd);
  };

  return {
    handleKeyDown,
  };
};
